import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import type { FactionId } from "@/data/factions.ts";
import type { RoleId } from "@/data/roles.ts";
import { persistenceKey } from "./roomCode.ts";
import {
  CommandStateSchema,
  MarkerSchema,
  OrderSchema,
  PlayerSchema,
  RouteSchema,
  SCHEMA_VERSION,
  SessionMetaSchema,
  SquadSchema,
  VoteSchema,
  type Marker,
  type Order,
  type Player,
  type Route,
  type SessionMeta,
  type Squad,
} from "./schemas.ts";
import type { CommandState, Vote } from "./command.ts";

/**
 * The shared session document.
 *
 * One Y.Doc per session code. The code is the identity: it names both the
 * IndexedDB database and the sync room, so a joining client opens the same
 * document the host is already writing to. No handshake, no temporary
 * document, no copying — the mistake that makes SubCoach's join flow a
 * 500 ms polling loop with a reconnect dance in the middle.
 *
 * Shape:
 *   meta     Y.Map  session settings
 *   players  Y.Map  everyone who has ever joined, keyed by player id
 *   command  Y.Map  the single "state" key holding command state
 *   votes    Y.Map  open ballot, keyed by voter id
 *   markers  Y.Map  map annotations
 *   orders   Y.Map  commander's tasking
 *
 * `players` is the roster, not the presence list. Who is actually connected
 * comes from awareness. Keeping them apart is what lets a commander crash,
 * stay in the roster, and reclaim the chair on reconnect.
 */

interface DocEntry {
  readonly doc: Y.Doc;
  readonly persistence: IndexeddbPersistence;
  synced: boolean;
  readonly waiters: Array<() => void>;
}

const docs = new Map<string, DocEntry>();

export function getSessionDoc(code: string): Y.Doc {
  return getEntry(code).doc;
}

function getEntry(code: string): DocEntry {
  const existing = docs.get(code);
  if (existing) return existing;

  const doc = new Y.Doc();
  const persistence = new IndexeddbPersistence(persistenceKey(code), doc);
  const entry: DocEntry = { doc, persistence, synced: false, waiters: [] };

  void persistence.once("synced", () => {
    entry.synced = true;
    for (const waiter of entry.waiters) waiter();
    entry.waiters.length = 0;
  });

  docs.set(code, entry);
  return entry;
}

export function isSessionSynced(code: string): boolean {
  return docs.get(code)?.synced ?? false;
}

/**
 * Resolve once local persistence has loaded.
 *
 * `IndexeddbPersistence` fires "synced" exactly once, and a listener added
 * afterwards never runs — hence the explicit flag and waiter list.
 */
export function waitForSessionSync(code: string): Promise<void> {
  const entry = getEntry(code);
  if (entry.synced) return Promise.resolve();
  return new Promise((resolve) => entry.waiters.push(resolve));
}

export function destroySessionDoc(code: string): void {
  const entry = docs.get(code);
  if (!entry) return;
  void entry.persistence.destroy();
  entry.doc.destroy();
  docs.delete(code);
}

// --- shared type accessors -------------------------------------------------

export function metaMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap("meta");
}

export function playersMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap("players");
}

export function commandMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap("command");
}

export function votesMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap("votes");
}

export function markersMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap("markers");
}

export function ordersMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap("orders");
}

export function squadsMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap("squads");
}

// --- meta ------------------------------------------------------------------

export function initialiseSession(
  doc: Y.Doc,
  code: string,
  mapId: string,
  faction: FactionId,
): void {
  const meta = metaMap(doc);
  // Only the first client to arrive seeds the session; everybody else inherits.
  if (meta.has("code")) return;

  doc.transact(() => {
    meta.set("code", code);
    meta.set("mapId", mapId);
    meta.set("faction", faction);
    meta.set("createdAt", Date.now());
    meta.set("schemaVersion", SCHEMA_VERSION);
  });
}

export function readMeta(doc: Y.Doc): SessionMeta | undefined {
  const meta = metaMap(doc);
  if (!meta.has("code")) return undefined;

  const result = SessionMetaSchema.safeParse({
    code: meta.get("code"),
    mapId: meta.get("mapId"),
    faction: meta.get("faction"),
    createdAt: meta.get("createdAt"),
    schemaVersion: meta.get("schemaVersion"),
  });

  if (!result.success) {
    console.error("Discarding invalid session meta", result.error);
    return undefined;
  }
  return result.data;
}

export function setSessionMap(doc: Y.Doc, mapId: string): void {
  doc.transact(() => metaMap(doc).set("mapId", mapId));
}

export function setSessionFaction(doc: Y.Doc, faction: FactionId): void {
  doc.transact(() => metaMap(doc).set("faction", faction));
}

// --- players ---------------------------------------------------------------

/**
 * Announce or refresh a player.
 *
 * Squad membership is deliberately NOT taken from the caller. This runs on
 * every reconnect and every identity tweak, and an unconditional write here
 * would silently throw people out of their squad each time they changed role
 * or their connection blipped. Membership only changes through
 * {@link assignPlayerSquad}.
 */
export function upsertPlayer(
  doc: Y.Doc,
  player: Omit<Player, "joinedAt" | "lastSeenAt" | "squad" | "vehicle">,
): void {
  const players = playersMap(doc);
  const existing = players.get(player.id) as Player | undefined;
  const now = Date.now();

  doc.transact(() => {
    players.set(player.id, {
      ...player,
      squad: existing?.squad ?? "",
      vehicle: existing?.vehicle ?? "",
      joinedAt: existing?.joinedAt ?? now,
      lastSeenAt: now,
    });
  });
}

export function setPlayerVehicle(doc: Y.Doc, playerId: string, vehicle: string): void {
  const players = playersMap(doc);
  const existing = players.get(playerId) as Player | undefined;
  if (!existing) return;
  doc.transact(() => players.set(playerId, { ...existing, vehicle, lastSeenAt: Date.now() }));
}

export function touchPlayer(doc: Y.Doc, playerId: string): void {
  const players = playersMap(doc);
  const existing = players.get(playerId) as Player | undefined;
  if (!existing) return;
  doc.transact(() => players.set(playerId, { ...existing, lastSeenAt: Date.now() }));
}

export function updatePlayerRole(doc: Y.Doc, playerId: string, role: RoleId): void {
  const players = playersMap(doc);
  const existing = players.get(playerId) as Player | undefined;
  if (!existing) return;
  doc.transact(() => players.set(playerId, { ...existing, role, lastSeenAt: Date.now() }));
}

/**
 * Move a player into a squad.
 *
 * Squads are not pre-seeded — this creates the squad record the first time
 * anybody joins that id, and hands leadership to whoever arrives first, so a
 * squad is never left without someone able to mark its orders done. Leaving a
 * squad passes the lead on rather than leaving a dangling reference.
 *
 * `squadName` only matters the moment the squad is created (see
 * {@link createSquad}); joining an id that already exists ignores it, since
 * a squad's name belongs to whoever named it first, not to every later
 * joiner.
 */
export function assignPlayerSquad(
  doc: Y.Doc,
  playerId: string,
  squadId: string,
  squadName?: string,
): void {
  const players = playersMap(doc);
  const squads = squadsMap(doc);
  const existing = players.get(playerId) as Player | undefined;
  if (!existing) return;
  if (existing.squad === squadId) return;

  const previousSquad = existing.squad;

  doc.transact(() => {
    players.set(playerId, { ...existing, squad: squadId, lastSeenAt: Date.now() });

    if (squadId) {
      const squad = squads.get(squadId) as Squad | undefined;
      if (!squad) {
        const name = squadName?.trim().slice(0, 24) || squadId;
        squads.set(squadId, { id: squadId, name, leadId: playerId, createdAt: Date.now() });
      } else if (!squad.leadId) {
        squads.set(squadId, { ...squad, leadId: playerId });
      }
    }

    if (previousSquad) {
      const old = squads.get(previousSquad) as Squad | undefined;
      const remaining = readPlayers(doc)
        .filter((entry) => entry.squad === previousSquad && entry.id !== playerId)
        .sort((a, b) => a.joinedAt - b.joinedAt);

      if (remaining.length === 0) {
        // Last one out turns the lights off. A squad with nobody in it is
        // not a slot waiting to be filled, it is clutter in the roster and
        // a target the commander could task by mistake. Its active order is
        // withdrawn too — there is nobody left to carry it out.
        squads.delete(previousSquad);
        const orders = ordersMap(doc);
        orders.forEach((value, key) => {
          const parsed = OrderSchema.safeParse(value);
          if (!parsed.success) return;
          if (parsed.data.squadId !== previousSquad) return;
          if (parsed.data.status !== "active") return;
          orders.set(key, { ...parsed.data, status: "cancelled" });
        });
      } else if (old?.leadId === playerId) {
        // Promote the longest-serving remaining member.
        squads.set(previousSquad, { ...old, leadId: remaining[0]?.id ?? "" });
      }
    }
  });
}

/**
 * Create a brand-new squad and join it in one step.
 *
 * The id is generated rather than derived from the name, so two players
 * typing the same squad name (both call it "Recon", say) get two distinct
 * squads rather than colliding on one — names are what humans read, ids are
 * only ever compared by the machine. Returns the new squad's id so the
 * caller can select it immediately.
 */
export function createSquad(doc: Y.Doc, playerId: string, name: string): string {
  const id = crypto.randomUUID();
  assignPlayerSquad(doc, playerId, id, name);
  return id;
}

export function setSquadLead(doc: Y.Doc, squadId: string, playerId: string): void {
  const squads = squadsMap(doc);
  const squad = squads.get(squadId) as Squad | undefined;
  if (!squad) return;
  doc.transact(() => squads.set(squadId, { ...squad, leadId: playerId }));
}

export function readSquads(doc: Y.Doc): Squad[] {
  const squads: Squad[] = [];
  squadsMap(doc).forEach((value) => {
    const result = SquadSchema.safeParse(value);
    if (result.success) squads.push(result.data);
  });
  return squads;
}

/**
 * Drop a player from the roster entirely.
 *
 * Routed through {@link assignPlayerSquad} first so the squad they were in
 * gets the same treatment as if they had left it themselves — lead handed
 * over, or the squad deleted if they were the last one in it.
 */
export function removePlayer(doc: Y.Doc, playerId: string): void {
  const existing = playersMap(doc).get(playerId) as Player | undefined;
  if (existing?.squad) assignPlayerSquad(doc, playerId, "");
  doc.transact(() => playersMap(doc).delete(playerId));
}

/** Every player in the roster. Invalid records are dropped, never thrown on. */
export function readPlayers(doc: Y.Doc): Player[] {
  const players: Player[] = [];
  playersMap(doc).forEach((value) => {
    const result = PlayerSchema.safeParse(value);
    if (result.success) players.push(result.data);
    else console.error("Discarding invalid player record", result.error);
  });
  return players.sort((a, b) => a.joinedAt - b.joinedAt);
}

export function readPlayer(doc: Y.Doc, playerId: string): Player | undefined {
  const result = PlayerSchema.safeParse(playersMap(doc).get(playerId));
  return result.success ? result.data : undefined;
}

// --- command ---------------------------------------------------------------

export function readCommandState(doc: Y.Doc): CommandState {
  const raw = commandMap(doc).get("state");
  if (raw === undefined) return { kind: "vacant" };

  const result = CommandStateSchema.safeParse(raw);
  if (!result.success) {
    console.error("Discarding invalid command state", result.error);
    return { kind: "vacant" };
  }
  return result.data;
}

export function writeCommandState(doc: Y.Doc, state: CommandState, clearVotes: boolean): void {
  doc.transact(() => {
    commandMap(doc).set("state", state);
    if (clearVotes) votesMap(doc).clear();
  });
}

// --- votes -----------------------------------------------------------------

export function castVote(doc: Y.Doc, voterId: string, inFavour: boolean): void {
  doc.transact(() => {
    // Keyed by voter, so re-voting replaces rather than stuffs the ballot.
    votesMap(doc).set(voterId, { voterId, inFavour, at: Date.now() });
  });
}

export function withdrawVote(doc: Y.Doc, voterId: string): void {
  doc.transact(() => votesMap(doc).delete(voterId));
}

export function readVotes(doc: Y.Doc): Vote[] {
  const votes: Vote[] = [];
  votesMap(doc).forEach((value) => {
    const result = VoteSchema.safeParse(value);
    if (result.success) votes.push(result.data);
  });
  return votes;
}

// --- markers ---------------------------------------------------------------

export function putMarker(doc: Y.Doc, marker: Marker): void {
  doc.transact(() => markersMap(doc).set(marker.id, marker));
}

export function deleteMarker(doc: Y.Doc, markerId: string): void {
  doc.transact(() => markersMap(doc).delete(markerId));
}

export function readMarkers(doc: Y.Doc): Marker[] {
  const markers: Marker[] = [];
  markersMap(doc).forEach((value) => {
    const result = MarkerSchema.safeParse(value);
    if (result.success) markers.push(result.data);
  });
  return markers.sort((a, b) => a.createdAt - b.createdAt);
}

// --- routes ----------------------------------------------------------------

export function routesMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap("routes");
}

export function putRoute(doc: Y.Doc, route: Route): void {
  doc.transact(() => routesMap(doc).set(route.id, route));
}

export function deleteRoute(doc: Y.Doc, routeId: string): void {
  doc.transact(() => routesMap(doc).delete(routeId));
}

export function readRoutes(doc: Y.Doc): Route[] {
  const routes: Route[] = [];
  routesMap(doc).forEach((value) => {
    const result = RouteSchema.safeParse(value);
    if (result.success) routes.push(result.data);
  });
  return routes.sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Wipe the tactical layer for a specific map — new match, same session.
 *
 * Orders become `cancelled` rather than deleted so the history survives.
 * Markers and routes are deleted outright since they have no meaningful
 * history. Squads, players and command are untouched — a team's structure
 * usually survives across matches.
 */
export function clearMapTactical(doc: Y.Doc, mapId: string): void {
  const orders = ordersMap(doc);
  const markers = markersMap(doc);
  const routes = routesMap(doc);

  doc.transact(() => {
    orders.forEach((value, key) => {
      const parsed = OrderSchema.safeParse(value);
      if (!parsed.success) return;
      const order = parsed.data;
      if ((order.mapId || mapId) !== mapId) return;
      if (order.status === "cancelled" || order.status === "complete") return;
      orders.set(key, { ...order, status: "cancelled" });
    });

    const markerKeysToDelete: string[] = [];
    markers.forEach((value, key) => {
      const parsed = MarkerSchema.safeParse(value);
      if (!parsed.success) return;
      if ((parsed.data.mapId || mapId) === mapId) markerKeysToDelete.push(key);
    });
    for (const key of markerKeysToDelete) markers.delete(key);

    const routeKeysToDelete: string[] = [];
    routes.forEach((value, key) => {
      const parsed = RouteSchema.safeParse(value);
      if (!parsed.success) return;
      if ((parsed.data.mapId || mapId) === mapId) routeKeysToDelete.push(key);
    });
    for (const key of routeKeysToDelete) routes.delete(key);
  });
}

// --- orders ----------------------------------------------------------------

/**
 * Issue an order, superseding whatever that squad was doing.
 *
 * A squad has exactly one current order. Stacking them would make "what am I
 * supposed to be doing" ambiguous, which is the one question this whole tool
 * exists to answer. Previous orders are cancelled rather than deleted so the
 * session keeps a record of what was asked and when.
 */
export function issueOrder(doc: Y.Doc, order: Order): void {
  const orders = ordersMap(doc);

  doc.transact(() => {
    orders.forEach((value, key) => {
      const parsed = OrderSchema.safeParse(value);
      if (!parsed.success) return;
      const existing = parsed.data;
      if (existing.squadId !== order.squadId) return;
      if (existing.status === "complete" || existing.status === "cancelled") return;
      orders.set(key, { ...existing, status: "cancelled" });
    });

    orders.set(order.id, order);
  });
}

export function putOrder(doc: Y.Doc, order: Order): void {
  doc.transact(() => ordersMap(doc).set(order.id, order));
}

/**
 * Amend an order in place, keeping its identity so the squad's card and the
 * pin on the map update rather than flicker through a delete-and-recreate.
 */
export function amendOrder(
  doc: Y.Doc,
  orderId: string,
  changes: Partial<Pick<Order, "kind" | "place" | "note" | "at" | "squadId">>,
): void {
  const orders = ordersMap(doc);
  const parsed = OrderSchema.safeParse(orders.get(orderId));
  if (!parsed.success) return;

  const existing = parsed.data;
  const next: Order = { ...existing, ...changes };

  const taskingChanged =
    next.kind !== existing.kind ||
    next.place !== existing.place ||
    next.note !== existing.note ||
    next.squadId !== existing.squadId ||
    next.at?.x !== existing.at?.x ||
    next.at?.y !== existing.at?.y;

  if (!taskingChanged) return;

  doc.transact(() => orders.set(orderId, next));
}

/** Withdraw an order. The squad goes back to having nothing to do. */
export function cancelOrder(doc: Y.Doc, orderId: string): void {
  const orders = ordersMap(doc);
  const parsed = OrderSchema.safeParse(orders.get(orderId));
  if (!parsed.success) return;
  doc.transact(() => orders.set(orderId, { ...parsed.data, status: "cancelled" }));
}

/** The order a squad is currently working to, if any. */
export function currentOrderFor(orders: readonly Order[], squadId: string): Order | undefined {
  return orders.find(
    (order) =>
      order.squadId === squadId && order.status !== "cancelled" && order.status !== "complete",
  );
}

export function setOrderStatus(doc: Y.Doc, orderId: string, status: Order["status"]): void {
  const orders = ordersMap(doc);
  const existing = orders.get(orderId) as Order | undefined;
  if (!existing) return;
  doc.transact(() => orders.set(orderId, { ...existing, status }));
}

export function readOrders(doc: Y.Doc): Order[] {
  const orders: Order[] = [];
  ordersMap(doc).forEach((value) => {
    const result = OrderSchema.safeParse(value);
    if (result.success) orders.push(result.data);
  });
  return orders.sort((a, b) => b.issuedAt - a.issuedAt);
}
